import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import w from './SafetyEnv.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';

// ============================================================================
//  안전 — 체감온도 통합관제 플랫폼  (/safety/platform)
//  [2026-10-08] 기획서 v7 3-2. 11개 콘텐츠 영역 순서 + 반복 CTA.
//  [2026-10-22 사용자] ★서브페이지 본문 '화이트화' — 블랙 본문이 블랙 푸터와 안 구분됨.
//    /safety(1호 화이트)와 동일 철학(SafetyEnv.module): 히어로=다크 유지 / 본문=화이트 / 섹션 흰↔#e4eaf2 교차.
//    표현 유지(색만 화이트에 맞춤): ①⑩준비중 ②셰브론 ③vs테이블 ④채널칩 ⑤~⑧틴트카드 ⑨풀컬러fill ⑪CTA틴트밴드+악센트.
//  SaaS=evstech-iot.com 통합관제([[evs-dashboard-saas-for-homepage]]). 데이터정책=1분전송·30분저장·14일로컬.
// ============================================================================

const HERO_IMG = '/images/hero/hero-safety.jpg';
const SLOGAN = '여러 현장의 체감온도와 위험상태를 한 화면에서 통합 관제하고, 경보·권한·이력·보고서까지 운영하는 체감온도 통합관제 플랫폼입니다.';

const Ic = ({ n }: { n: string }) => <div className={w.iconWrap}>{GUIDE_ICON[n]}</div>;
const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};
function Soon({ label, minHeight }: { label: string; minHeight?: number }) {
  return (
    <div className={w.soon} role="img" aria-label={`${label} 준비중`} style={minHeight ? { minHeight } : undefined}>
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

export default function SafetyPlatform() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);

  const Cta = ({ left }: { left?: boolean }) => (
    <div className={w.ctaRow} style={left ? { justifyContent: 'flex-start' } : undefined}>
      <Button variant="pill" onClick={() => nav('/safety/platform')}>데모 체험</Button>
      <a className={w.ctaGhost} onClick={() => nav('/support/consult')}>도입 상담</a>
      <a className={w.ctaGhost} onClick={() => nav('/support/inquiry?from=/safety/platform')}>요금 문의</a>
    </div>
  );

  return (
    <main className={w.page}>
      <PageLoader />

      <header className={w.hero}>
        <div className={w.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={w.heroOverlay} />
        <HeroCrumbs items={['HOME', '안전', '통합관제 플랫폼']} />
        <div className={w.heroInner}>
          <h1 className={w.heroTitle}>체감온도 통합관제 플랫폼</h1>
          <p className={w.heroSub}>{SLOGAN}</p>
        </div>
      </header>

      {/* ① 실제 대시보드 (준비중) */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>DASHBOARD</p>
            <h2 className={w.h2}>한 화면에서 모든 현장을 관제</h2>
            <p className={w.lead}>사업장·장비·위험 현황을 실시간 대시보드로 표시합니다. 체험용 대시보드는 개인정보 없는 가상 데이터로 제공됩니다.</p>
            <Soon label="실제 대시보드 화면" minHeight={320} />
            <div style={{ marginTop: 30 }}><Cta left /></div>
          </Reveal>
        </div>
      </section>

      {/* ② 실시간 관제 — 셰브론 배너 */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>REAL-TIME</p>
            <h2 className={w.h2}>실시간 관제</h2>
            <p className={w.lead}>사업장·공정·장비별 측정값과 통신 상태를 실시간으로 모니터링합니다.</p>
            <div className={w.chevron}>
              {[
                { n: '01', t: '측정값 모니터링', i: 'monitor', d: '장비별 체감온도·기온·습도를 실시간으로 확인합니다.', c: w.cBlue },
                { n: '02', t: '위험 현황', i: 'alert', d: '관심·주의·경고·위험 4단계를 현장별로 한눈에 파악합니다.', c: w.cCoral },
                { n: '03', t: '통신 상태', i: 'signal', d: '단말 연결·미수신 상태를 표시해 장애를 빠르게 인지합니다.', c: w.cTeal },
              ].map((x) => (
                <div key={x.n} className={`${w.chevronStep} ${x.c}`}>
                  <div className={w.chevronIco}>{GUIDE_ICON[x.i]}</div>
                  <span className={w.chevronNum}>{x.n}</span>
                  <h3>{x.t}</h3><p>{x.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ③ 차별화 비교 — vs 테이블 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>WHY INTEGRATED</p>
            <h2 className={w.h2}>단독 표시형 vs EVS 통합관제형</h2>
            <p className={w.lead}>현장에 숫자만 띄우는 단독 표시형과, 여러 현장을 한 화면에서 관리·증빙까지 하는 EVS 통합관제형의 차이입니다.</p>
            <div className={w.tableWrap}>
              <table className={w.vsTable}>
                <thead><tr><th>항목</th><th>일반 단독 표시형</th><th className={w.vEvs}>EVS 통합관제형</th></tr></thead>
                <tbody>
                  {[
                    ['관제 범위', '단일 현장 표시', '다중 현장 통합 관제'],
                    ['사용자 권한', '없음', '역할별 권한(운영자·총괄·담당자)'],
                    ['이력 관리', '없음 / 제한', '설정·통신·경보·작업 이력 전체'],
                    ['보고서', '수기 작성', '월간 자동생성(AI 항목 포함)'],
                    ['알림', '제한적', 'SMS·카카오·앱·이메일 다채널'],
                  ].map((r, i) => (
                    <tr key={i}>
                      <td className={w.vName}>{r[0]}</td>
                      <td className={w.vGen}><span className={w.vDash}>–</span>{r[1]}</td>
                      <td className={w.vEvs}><span className={w.vMark}>✓</span>{r[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ④ 다채널 알림 — 채널 칩 */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>ALERT</p>
            <h2 className={w.h2}>다채널 알림</h2>
            <p className={w.lead}>위험 단계·장애 발생 시 담당자에게 다채널로 즉시 전달합니다. 현장 전광판은 중계기 연계로 제공 예정입니다.</p>
            <div className={w.chips}>
              {[
                { l: 'SMS', i: 'sms' },
                { l: '카카오톡', i: 'kakao' },
                { l: '앱푸시', i: 'apppush' },
                { l: '이메일', i: 'email' },
                { l: '전광판 (예정)', i: 'display', soon: true },
              ].map((c) => (
                <span key={c.l} className={w.chip} style={c.soon ? { opacity: .55 } : undefined}>
                  <span className={w.chipIco}>{GUIDE_ICON[c.i]}</span>{c.l}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ⑤~⑧ 운영 기능 — 틴트 카드 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>OPERATION</p>
            <h2 className={w.h2}>운영 기능</h2>
            <p className={w.lead}>현장 운영에 필요한 설정·권한·이력·보고 기능을 제공합니다. 실제 설정·보고서 화면은 준비 중입니다.</p>
            <div className={`${w.grid} ${w.grid4}`}>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="calendar" /><h3>근무시간 관리</h3><p>근무·휴게·교대 시간과 공정별 알림 필터를 설정해 불필요한 알림을 줄입니다.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.solid}`}><Ic n="toggle" /><h3>권한 관리</h3><p>최고운영자·총괄·공정담당자 역할별로 접근 권한을 구분합니다.</p></div>
              <div className={`${w.card} ${w.cAmber} ${w.solid}`}><Ic n="database" /><h3>이력 관리</h3><p>설정 변경·통신·경보·사용자 작업 이력을 기록해 추적합니다.</p></div>
              <div className={`${w.card} ${w.cBlue} ${w.solid}`}><Ic n="doc" /><h3>월간 보고서</h3><p>월간 보고서를 자동 생성하며, AI 분석 항목을 포함합니다.</p></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ⑨ 데이터 안정성 — ★핵심 풀컬러 fill 카드 */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>RELIABILITY</p>
            <h2 className={w.h2}>데이터 안정성</h2>
            <p className={w.lead}>통신이 끊겨도 데이터는 보존되고, 법정기록 주기로 자동 저장됩니다. 플랫폼의 핵심 신뢰 요소입니다.</p>
            <div className={`${w.grid} ${w.grid3}`}>
              <div className={`${w.card} ${w.cSky} ${w.fill}`}><Ic n="signal" /><h3>1분 전송</h3><p>측정값을 1분 주기로 클라우드에 전송합니다.</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.fill}`}><Ic n="calendar" /><h3>30분 기록</h3><p>법정기록으로 주기적으로 자동 저장합니다.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.fill}`}><Ic n="database" /><h3>14일 로컬 저장</h3><p>통신 장애 시에도 단말에 데이터를 보존합니다.</p></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ⑩ 요금제 (준비중) */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>PRICING</p>
            <h2 className={w.h2}>요금제</h2>
            <p className={w.lead}>Pro·AI 요금제와 통신 방식(Wi-Fi·Ethernet·LTE)별 비용 공개 범위를 안내합니다.</p>
            <Soon label="Pro·AI 요금제" />
          </Reveal>
        </div>
      </section>

      {/* ⑪ 체험 신청 — CTA 틴트밴드 + 상단 시안 악센트 */}
      <section className={w.finalCta}>
        <div className={w.inner}>
          <Reveal>
            <h2 className={w.h2}>지금 바로 체험해 보세요</h2>
            <p className={w.lead}>개인정보 없는 샘플 데이터로 통합관제 대시보드를 체험할 수 있습니다.</p>
            <Cta />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
